/* eslint-disable @typescript-eslint/no-explicit-any */
import Big from "big.js";
import React, { useEffect, useRef, useState } from "react";
import NumberFormat, {
  NumberFormatProps,
  SourceInfo,
} from "react-number-format";
import { useLanguage } from "../../../store";

interface PercentageInputPropsType {
  $hidePercentageSymbol?: boolean;
  $maxValue?: number | null;
  $focusOnMount?: boolean;
  $selectAllOnFocus?: boolean;
  // All other props
  [x: string]: any;
}

export const PercentageInput: React.FC<PercentageInputPropsType> = ({
  onValueChange,
  ...props
}: NumberFormatProps) => {
  PercentageInput.defaultProps = {
    $hidePercentageSymbol: false,
    $maxValue: null,
    $focusOnMount: false,
    $selectAllOnFocus: false,
  };

  const [language] = useLanguage();

  const inputRef = useRef<HTMLDivElement>(null);

  const [value, setValue] = useState<string | number>(
    Big(props.value || 0)
      .times(100)
      .toNumber() || ""
  );

  const handleChange = (v: any, sourceInfo: SourceInfo): void => {
    // Sincronizes inside and outside states
    setValue(Big(v.value).times(100).toNumber());

    if (onValueChange) {
      onValueChange(
        { ...v, floatValue: Big(v.floatValue).div(100).toNumber() },
        sourceInfo
      );
    }
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (props.$focusOnMount && inputRef?.current?.firstChild) {
      const input = inputRef.current.firstChild as HTMLInputElement;
      input.focus();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // const handeOnFocus = (): void => {
  //   // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //   // @ts-ignore
  //   if (props.$selectAllOnFocus && inputRef?.current?.firstChild) {
  //     const input = inputRef.current.firstChild as HTMLInputElement;
  //     input.select();
  //   }
  // };

  const onFocus = (): void => {
    if (inputRef?.current?.firstChild) {
      const input = inputRef?.current?.firstChild as HTMLInputElement;
      const valueSize = String(input.value).length;
      input.select();
      input.selectionEnd = valueSize - 5;
      input.selectionStart = 0;
    }
  };

  const percentageFormatter = (formattedValue: any): string => {
    const br = { style: "currency", currency: "BRL" };

    let percentageValue = formattedValue;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const maxValue = props.$maxValue;

    if (maxValue !== null) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (percentageValue > Number(maxValue) * 1000) {
        percentageValue = Number(value) / 100;
      }
    }

    return (
      new Intl.NumberFormat(language || "pt-BR", br)
        .formatToParts(
          Big(percentageValue || 0)
            .div(100)
            .toNumber()
        )
        .map((parameter) =>
          parameter.type !== "literal" && parameter.type !== "currency"
            ? parameter.value
            : ""
        )
        .join("")
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        .concat(`${props.$hidePercentageSymbol ? "" : " %"}`)
    );
  };

  const keyDown = (e: any): void => {
    // Keeps the cursor position on erase if the value is === 0
    if (e.code === "Backspace" && !value) {
      e.preventDefault();
    }
    // Sets the value to 0 and prevents the default so curor remains in "cents" area
    if (e.code === "Backspace" && value < 1000) {
      e.preventDefault();
      setValue(0);
    }
  };

  // Sincronizes inside and outside states
  useEffect(
    () =>
      setValue(
        Big(props.value || 0)
          .times(100)
          .toNumber()
      ),
    [props.value]
  );

  // Debug Tool
  //
  // const logOnFocus = (): void => {
  //   console.log(">>> Prop recebida:", props.value);
  // };

  // const handleClick = (e: any): void => {
  //   try {
  //     if (value === 0) {
  //       const valueSize = String(e.target.value).length;
  //       e.target.selectionEnd = valueSize;
  //       e.target.selectionStart = valueSize;
  //     }
  //   } catch {
  //     // ignore error
  //   }
  // };

  return (
    <NumberFormat
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      value={Big(Number(value)).div(100).toNumber()}
      format={percentageFormatter}
      onValueChange={handleChange}
      allowEmptyFormatting
      onKeyDown={keyDown}
      // onFocus={logOnFocus}
      onFocus={onFocus}
      // onClick={handleClick}
      getInputRef={inputRef}
    />
  );
};

export default PercentageInput;
